import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { NavbarTab } from 'src/components/layout/app-navbar';

/**
 * Base path for survey group results (overview), without `/compare` or `/trends`.
 */
export function buildSurveyGroupResultsBasePath(surveyGroupId: string, clientId: string | undefined): string {
  if (clientId) {
    return `/client/${clientId}/results/group/${surveyGroupId}`;
  }
  return `/results/group/${surveyGroupId}`;
}

export function buildSurveyGroupComparePath(surveyGroupId: string, clientId: string | undefined): string {
  return `${buildSurveyGroupResultsBasePath(surveyGroupId, clientId)}/compare`;
}

export function buildSurveyGroupTrendsPath(surveyGroupId: string, clientId: string | undefined): string {
  return `${buildSurveyGroupResultsBasePath(surveyGroupId, clientId)}/trends`;
}

function appendQuery(base: string, search: string): string {
  if (!search || search === '?') {
    return base;
  }
  return `${base}${search.startsWith('?') ? search : `?${search}`}`;
}

/**
 * Overview / Compare / Trends tabs with navigation; preserves `groupSurveys` and other query params.
 */
export function useSurveyGroupResultsTabs(surveyGroupId: string | undefined, clientId: string | undefined): {
  tabs: NavbarTab[];
  activeTab: string;
} {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const searchString = searchParams.toString();
  const querySuffix = searchString ? `?${searchString}` : '';

  const isCompare = Boolean(surveyGroupId && pathname.endsWith('/compare'));
  const isTrends = Boolean(surveyGroupId && pathname.endsWith('/trends'));

  const goOverview = useCallback(() => {
    if (!surveyGroupId) {
      return;
    }
    navigate(appendQuery(buildSurveyGroupResultsBasePath(surveyGroupId, clientId), querySuffix));
  }, [navigate, surveyGroupId, clientId, querySuffix]);

  const goCompare = useCallback(() => {
    if (!surveyGroupId) {
      return;
    }
    navigate(appendQuery(buildSurveyGroupComparePath(surveyGroupId, clientId), querySuffix));
  }, [navigate, surveyGroupId, clientId, querySuffix]);

  const goTrends = useCallback(() => {
    if (!surveyGroupId) {
      return;
    }
    navigate(appendQuery(buildSurveyGroupTrendsPath(surveyGroupId, clientId), querySuffix));
  }, [navigate, surveyGroupId, clientId, querySuffix]);

  const tabs: NavbarTab[] = useMemo(
    () => [
      { id: 'overview', key: 'ALL', label: 'Overview', onClick: goOverview },
      { id: 'compare', key: 'ALL', label: 'Compare', onClick: goCompare },
      { id: 'trends', key: 'ALL', label: 'Trends', onClick: goTrends },
    ],
    [goOverview, goCompare, goTrends]
  );

  let activeTab = 'overview';
  if (isCompare) {
    activeTab = 'compare';
  } else if (isTrends) {
    activeTab = 'trends';
  }

  return {
    tabs,
    activeTab,
  };
}
